import { useEffect, useState } from 'react';
import orgApi from '../../api/organizations';
import ConfirmModal from '../UI/ConfirmModal';
import Drawer from '../UI/Drawer';
import {
  primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, fieldLabel, fieldLabelStyle,
  monoLabel, errMessage, formatDate,
} from '../UI/ink';

const OrgDetailsDrawer = ({ open, onClose, tenantId, org, myRole, canEdit, onSaved }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(org?.name || '');
    setError(null);
    setDeleteOpen(false);
    setConfirmText('');
  }, [open, org]);

  const orgName = org?.name || 'this organisation';

  const save = async () => {
    if (!name.trim()) { setError('Organisation name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await orgApi.updateOrg(tenantId, { name: name.trim() });
      await onSaved();
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      const response = await orgApi.deleteOrg(tenantId);
      setDeleteOpen(false);
      // The delete response already re-issued the session cookie for the next org.
      if (response?.remaining_organizations?.length) {
        try {
          await orgApi.switchOrg(response.remaining_organizations[0].id);
        } catch {
          // Fall through to a reload; the app lands on the no-org state.
        }
      }
      window.location.assign('/org/members');
    } catch (err) {
      setDeleteOpen(false);
      setError(errMessage(err, 'Failed to delete organisation.'));
    }
  };

  const facts = [
    { label: 'Tenant ID', value: tenantId },
    { label: 'Created', value: formatDate(org?.created_at, true) },
    { label: 'Your role', value: myRole || '—' },
    { label: 'Status', value: org && !org.active ? 'Inactive' : 'Active' },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="ORGANISATION"
      title="Organisation details"
      subtitle={`Visible to every member of ${orgName}.`}
      footer={(
        <>
          <span />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
            {canEdit && (
              <button type="button" onClick={save} disabled={saving || !org} className={primaryBtn} style={primaryBtnStyle}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </div>
        </>
      )}
    >
      <div className="flex flex-col gap-5 min-h-full">
        {error && (
          <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
            {error}
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="org-name" className={fieldLabel} style={fieldLabelStyle}>Organisation name</label>
          <input id="org-name" type="text" value={name} onChange={e => setName(e.target.value)} disabled={!canEdit || !org} className={inputCls} style={inputStyle} />
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-4 p-4 rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
          {facts.map(f => (
            <div key={f.label} className="flex flex-col gap-1 min-w-0">
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{f.label}</span>
              <span className="font-mono text-[13px] break-all">{f.value}</span>
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="mt-auto flex flex-col gap-3 p-4 rounded-[10px] border" style={{ borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
            <span className={monoLabel} style={{ color: 'var(--sev-crit-fg)' }}>DANGER ZONE</span>
            <span className="text-[13.5px] font-semibold">Delete this organisation</span>
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
              Permanently removes {orgName}’s assets, events, members and audit log. You’ll be asked to type the organisation name to confirm.
            </span>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={!org}
              className="self-start h-10 px-3.5 rounded-[10px] border text-[13px] font-medium disabled:opacity-50"
              style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--ink-text)' }}
            >
              Delete organisation…
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteOpen}
        title="Delete organisation"
        danger
        confirmLabel="Delete permanently"
        disabled={confirmText !== org?.name}
        onConfirm={doDelete}
        onCancel={() => { setDeleteOpen(false); setConfirmText(''); }}
      >
        <div className="mt-2">
          <p className="text-sm mb-3" style={{ color: 'var(--ink-muted)' }}>
            This can’t be undone. Type <strong style={{ color: 'var(--ink-text)' }}>{org?.name}</strong> to confirm:
          </p>
          <input
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder={org?.name}
            aria-label="Organisation name"
            className={inputCls}
            style={inputStyle}
          />
        </div>
      </ConfirmModal>
    </Drawer>
  );
};

export default OrgDetailsDrawer;
